import { supabase } from '../config/supabase';

export interface ClubTransfer {
  club_id: string;
  new_manager_id: string;
}

export interface SoleManagedClubMember {
  user_id: string;
  display_name: string;
}

export interface SoleManagedClub {
  club_id: string;
  club_name: string;
  members: SoleManagedClubMember[];
}

export interface AccountDeletionPreview {
  active_listings_count: number;
  active_transactions_count: number;
  sole_managed_clubs: SoleManagedClub[];
}

export const accountDeletionService = {
  async getPreview(): Promise<AccountDeletionPreview> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const [listingsResult, txResult, managedClubsResult] = await Promise.all([
      supabase
        .from('clothing_items')
        .select('*', { count: 'exact', head: true })
        .eq('seller_id', user.id)
        .in('status', ['available', 'reserved']),
      supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .in('status', ['pending', 'buyer_confirmed']),
      supabase
        .from('user_clubs')
        .select('club_id, clubs(name)')
        .eq('user_id', user.id)
        .eq('role', 'manager'),
    ]);

    const soleManagedClubs: SoleManagedClub[] = [];

    for (const mc of managedClubsResult.data ?? []) {
      const { count: otherManagerCount, error: managerError } = await supabase
        .from('user_clubs')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', mc.club_id)
        .neq('user_id', user.id)
        .eq('role', 'manager');

      if (managerError) throw new Error(`Failed to check managers: ${managerError.message}`);

      if ((otherManagerCount ?? 0) === 0) {
        const { data: members, error: membersError } = await supabase
          .from('user_clubs')
          .select('user_id, profiles(first_name, last_name, email)')
          .eq('club_id', mc.club_id)
          .neq('user_id', user.id);

        if (membersError) throw new Error(`Failed to load members: ${membersError.message}`);

        const club = mc.clubs as unknown as { name: string } | null;

        soleManagedClubs.push({
          club_id: mc.club_id,
          club_name: club?.name ?? 'Onbekende club',
          members: (members ?? []).map((m) => {
            const profile = m.profiles as unknown as { first_name?: string; last_name?: string; email: string } | null;
            const name = profile
              ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() || profile.email
              : 'Onbekend lid';
            return { user_id: m.user_id, display_name: name };
          }),
        });
      }
    }

    return {
      active_listings_count: listingsResult.count ?? 0,
      active_transactions_count: txResult.count ?? 0,
      sole_managed_clubs: soleManagedClubs,
    };
  },

  async deleteAccount(clubTransfers: ClubTransfer[]): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ club_transfers: clubTransfers }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error ?? 'Account deletion failed');
    }
  },
};
